// Polyline decoding utility for OSRM routes
export function decodePolyline(encoded: string): [number, number][] {
  const coordinates: [number, number][] = [];
  let index = 0;
  let lat = 0;
  let lng = 0;

  while (index < encoded.length) {
    let b: number;
    let shift = 0;
    let result = 0;

    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;
    lat += deltaLat;

    shift = 0;
    result = 0;

    do {
      b = encoded.charCodeAt(index++) - 63;
      result |= (b & 0x1f) << shift;
      shift += 5;
    } while (b >= 0x20);

    const deltaLng = result & 1 ? ~(result >> 1) : result >> 1;
    lng += deltaLng;

    coordinates.push([lat / 1e5, lng / 1e5]);
  }

  return coordinates;
}

interface OSRMWaypoint {
  location: [number, number];
  name?: string;
}

interface OSRMStep {
  geometry: string;
  distance: number;
  duration: number;
}

interface OSRMLeg {
  steps: OSRMStep[];
  distance: number;
  duration: number;
}

interface OSRMRouteDetails {
  geometry: string;
  legs: OSRMLeg[];
  distance: number;
  duration: number;
}

interface OSRMRoute {
  routes: OSRMRouteDetails[];
  waypoints?: OSRMWaypoint[];
}

export function parseOSRMRoute(osrmRoute: OSRMRoute) {
  if (!osrmRoute?.routes?.[0]) return null;

  const route = osrmRoute.routes[0];
  const legs = route.legs || [];
  const waypoints = osrmRoute.waypoints || [];

  // Extract all polyline steps from each leg
  let routeCoordinates: [number, number][] = [];

  legs.forEach((leg) => {
    if (leg.steps && leg.steps.length > 0) {
      leg.steps.forEach((step) => {
        if (step.geometry) {
          // Decode each step's geometry and add to route coordinates
          const stepCoordinates = decodePolyline(step.geometry);
          // If this isn't the first step, remove the first point to avoid duplicates
          if (routeCoordinates.length > 0 && stepCoordinates.length > 0) {
            routeCoordinates = [
              ...routeCoordinates,
              ...stepCoordinates.slice(1),
            ];
          } else {
            routeCoordinates = [...routeCoordinates, ...stepCoordinates];
          }
        }
      });
    }
  });

  // If no steps were found, fall back to the main route geometry
  if (routeCoordinates.length === 0 && route.geometry) {
    routeCoordinates = decodePolyline(route.geometry);
  }

  // Extract waypoints (start and end points)
  const routeWaypoints = waypoints.map((waypoint: OSRMWaypoint) => ({
    coordinates: [waypoint.location[1], waypoint.location[0]], // [lat, lng]
    name: waypoint.name || "Waypoint",
  }));

  return {
    coordinates: routeCoordinates,
    waypoints: routeWaypoints,
    distance: route.distance,
    duration: route.duration,
  };
}
